import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ProjectContextService, STORAGE_PROJECT_KEY } from './project-context.service';
import { UserPreferencesService } from './user-preferences.service';

/**
 * Validates that the :projectId param in /p/:projectId/... is accessible to the user
 * and sets it as the active project in ProjectContextService.
 */
export const projectAccessGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const projectContext = inject(ProjectContextService);
  const router = inject(Router);

  // Retrieve projectId from route param (or parent route param)
  let projectId = route.paramMap.get('projectId');
  if (!projectId && route.parent) {
    projectId = route.parent.paramMap.get('projectId');
  }

  if (!projectId) {
    return router.parseUrl('/projects');
  }

  return projectContext.setActiveProjectId(projectId).pipe(
    map(project => {
      if (project && project.id) {
        if (project.role === 'outside_user') {
          return router.parseUrl(`/support/${projectId}/portal`);
        }
        return true;
      }
      // Project not found or user lacks membership
      console.warn(`projectAccessGuard: User lacks access to project ${projectId}, redirecting to /projects`);
      return router.parseUrl('/projects');
    }),
    catchError(err => {
      console.error('projectAccessGuard error:', err);
      return of(router.parseUrl('/projects'));
    })
  );
};

/**
 * Handles root `/` and legacy paths (`/t/...`, `/u/...`) without a :projectId in the URL.
 * Automatically resolves the appropriate project:
 * - 0 projects: auto-creates primary project and redirects to /p/:newId/t/roadmap
 * - 1 project: redirects to /p/:projectId/<targetPath>
 * - Multiple projects:
 *     - if valid active project saved in localStorage, redirects to /p/:savedId/<targetPath>
 *     - otherwise redirects to /projects selector
 */
export const projectRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const projectContext = inject(ProjectContextService);
  const preferencesService = inject(UserPreferencesService);
  const router = inject(Router);

  // Determine relative target path after /p/:projectId/
  let rawUrl = state.url || '';
  if (rawUrl.startsWith('/')) {
    rawUrl = rawUrl.substring(1);
  }

  return projectContext.loadUserProjects(false).pipe(
    switchMap(projects => {
      // 0 projects: ensureProject creates primary project
      if (!projects || projects.length === 0) {
        return projectContext.ensureProject().pipe(
          map(created => {
            const landing = preferencesService.getLandingPageRoute(created.id);
            return router.parseUrl(landing);
          })
        );
      }

      // Helper to compute target path for a given projectId
      const getTargetUrl = (projId: string): string => {
        if (rawUrl && (rawUrl.startsWith('t/') || rawUrl.startsWith('u/'))) {
          return `/p/${projId}/${rawUrl}`;
        }
        return preferencesService.getLandingPageRoute(projId);
      };

      // 1 project: go directly to it
      if (projects.length === 1) {
        projectContext.setCurrentProject(projects[0]);
        return of(router.parseUrl(getTargetUrl(projects[0].id)));
      }

      // Multiple projects: check localStorage
      const savedId = localStorage.getItem(STORAGE_PROJECT_KEY);
      const matched = savedId ? projects.find(p => p.id === savedId) : null;

      if (matched) {
        projectContext.setCurrentProject(matched);
        return of(router.parseUrl(getTargetUrl(matched.id)));
      }

      // Multiple projects with no saved project selection -> show Project Selector
      return of(router.parseUrl('/projects'));
    }),
    catchError(err => {
      console.warn('projectRedirectGuard error, falling back to /projects', err);
      return of(router.parseUrl('/projects'));
    })
  );
};
